import { GlobalConfigType } from 'actions'
import { LayoutLoading } from 'components/LayoutLoading'
import { feePercent, renderStripeAmountDetails, Stripe } from 'components/Stripe'
import { StripeWithMethod } from 'pages/Payment/StripeWithMethod'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  getActivePaymentProvider,
  getAvailablePaymentMethods,
  PaymentProvider,
  StripeMethod,
  updatePaymentStatus,
} from 'services'

/**
 * The page's "Type" field values, which the payment history reads back as the payment type.
 * Built on call, not at module load: this file sits in an import cycle with `services`, and
 * reading StripeMethod while the bundle is still initializing crashes the whole app.
 */
const paymentMethods = (): Record<string, StripeMethod> => ({
  ACH: StripeMethod.us_bank_account,
  'Credit Card': StripeMethod.card,
  'Pinless Debit': StripeMethod.pinless_debit,
})
const typeForMethod = (m: StripeMethod) => {
  const methods = paymentMethods()
  return Object.keys(methods).find((key) => methods[key] === m) || ''
}

export const StripePay = ({
  data,
  onPay,
  onTypeChange,
}: {
  data: Record<string, any>
  onPay: Function
  /** Keeps the page's "Type" field in step when the payer switches method on the checkout. */
  onTypeChange?: (type: string) => void
}) => {
  const rulesConfig = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.RulesConfig] || {})
  // Whether any method in this area runs through USIO. Such a payment goes through the shared
  // checkout, which offers card or ACH on the payment itself (as the servicing phone payment does)
  // and quotes USIO's own convenience fee instead of the Stripe percentage used below.
  const [usesUsio, setUsesUsio] = useState<boolean | null>(null)
  const [isLoading, setLoading] = useState(false)
  const [isCompleted, setCompleted] = useState(false)

  useEffect(() => setLoading(false), [])

  const method = paymentMethods()[data.type] || StripeMethod.card

  useEffect(() => {
    getActivePaymentProvider(data.payOrderType)
      .then((res) => {
        const providers = [res?.provider, ...Object.values(res?.methods || {}).map((item) => item?.provider)]
        setUsesUsio(providers.includes(PaymentProvider.usio))
      })
      .catch(() => setUsesUsio(rulesConfig.paymentProvider === PaymentProvider.usio))
  }, [data.payOrderType])

  const availableMethods = useMemo(
    () =>
      getAvailablePaymentMethods(
        data.payOrderType,
        rulesConfig.creditCardPaymentMethods,
        rulesConfig.achBankTransferPaymentMethods,
        rulesConfig.pinlessDebitPaymentMethods,
      ),
    [data.payOrderType, rulesConfig],
  )

  const onCompleted = (intentId: string) => {
    setLoading(true)
    updatePaymentStatus(intentId)
      .then(({ status, message }) => {
        setCompleted(true)
        onPay(true, intentId, status, message)
      })
      .finally(() => setLoading(false))
  }

  const feeKey = method === StripeMethod.pinless_debit ? 'Credit Card' : data.type

  if (usesUsio === null) return <div className="py-6 text-gray-500 text-[15px]">Loading payment options…</div>

  if (usesUsio) {
    return (
      <div className="text-gray-600 mb-4 text-md max-w-3xl">
        <div className="text-sm bg-gray-50 p-4 rounded shadow1">
          <StripeWithMethod
            availableMethods={availableMethods}
            defaultMethod={method}
            // `data.orderType` on the feasibility page is the valuation's order type, not the payment's.
            paymentData={{
              ...data,
              orderType: data.payOrderType,
              refId: data.refId,
              email: data.email,
              amount: data.amount,
            }}
            // The history records the payment under `type`, so it has to name the method actually charged.
            dataForMethod={(m) => ({ type: typeForMethod(m) || data.type })}
            onMethodChange={(m) => {
              const type = typeForMethod(m)
              if (type && type !== data.type) onTypeChange?.(type)
            }}
            onCompleted={(intentId: string, status: string, message: string) =>
              onPay(true, intentId, status, message)
            }
          />
        </div>
      </div>
    )
  }

  if (isCompleted) return renderStripeAmountDetails(data.amount, feeKey)

  return (
    <div>
      <LayoutLoading show={isLoading} />
      <div className="text-gray-600 mb-4 text-md max-w-3xl">
        <div className="grid grid-cols-2 text-[14.5px] mb-4"></div>
        <div className="text-sm bg-gray-50 p-4 rounded shadow1">
          {renderStripeAmountDetails(data.amount, feeKey)}
          <Stripe
            key={method}
            orderType={data.payOrderType}
            email={data.email}
            refId={data.refId}
            amount={Number(Number(data.amount * (1 + feePercent[data.type])).toFixed(2))}
            method={method as any}
            data={data}
            setLoading={setLoading}
            onCompleted={onCompleted}
          />
        </div>
      </div>
    </div>
  )
}
